import React from "react";
import { useNavigate } from "react-router-dom";

type ReportItem = {
  reportId: number;
  reportName: string;
  severityCategory: string;
  submittedDate: string;
};

type RetestItem = {
  retestId: number;
  reportTitle: string;
  status: string;
  createdAt?: string;
};

function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, string> = {
    low: "bg-emerald-500",
    medium: "bg-amber-500",
    high: "bg-orange-500",
    critical: "bg-red-500",
    info: "bg-blue-500",
    informational: "bg-blue-500",
    none: "bg-blue-500"
  };
  const key = severity?.toLowerCase?.() || "";
  const color = map[key] || "bg-gray-400";
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-white text-xs font-medium ${color}`}>
      {key === "none" ? "Info" : (severity?.charAt(0).toUpperCase() + severity?.slice(1))}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color = status === "Fix Failed" ? "bg-red-500" : "bg-blue-500";
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-white text-xs font-medium ${color}`}>
      {status}
    </span>
  );
}

export default function ActionableTabs({
  reports,
  retests
}: {
  reports: ReportItem[];
  retests: RetestItem[];
}) {
  const [tab, setTab] = React.useState<"reports" | "retests">("reports");
  const navigate = useNavigate();

  const formatDate = (value?: string) => {
    if (!value) return "—";
    const str = String(value);
    // Normalize common SQL datetime format "YYYY-MM-DD HH:mm:ss"
    const normalized = str.includes(" ") && !str.includes("T") ? str.replace(" ", "T") : str;
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB");
  };

  React.useEffect(() => {
    if (tab === "retests") {
      try {
        console.log("[ActionableTabs] Retests sample:", (retests || []).slice(0, 5));
      } catch {}
    }
  }, [tab, retests]);

  const renderHeader = () => (
    <div className="flex items-center justify-between border-b border-gray-200">
      <div className="flex gap-10 px-6 pt-4">
        <button
          className={`pb-3 text-lg font-semibold ${
            tab === "reports" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-700"
          }`}
          onClick={() => setTab("reports")}
        >
          Actionable Reports
        </button>
        <button
          className={`pb-3 text-lg font-semibold ${
            tab === "retests" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-700"
          }`}
          onClick={() => setTab("retests")}
        >
          Actionable Retest
        </button>
      </div>
    </div>
  );

  const renderTableHead = () => (
    <div className="grid grid-cols-12 items-center border-b bg-gray-50 px-6 py-4 text-sm font-semibold text-gray-700">
      <div className="col-span-7">Report name</div>
      <div className="col-span-3">{tab === "reports" ? "Severity" : "Status"}</div>
      <div className="col-span-2">Date</div>
    </div>
  );

  const items = tab === "reports" ? reports : retests;

  const renderRow = (item: any, idx: number) => (
    <div key={idx} className="grid grid-cols-12 items-center border-b px-6 py-6 text-sm text-gray-800">
      <div
        className="col-span-7 leading-6 cursor-pointer hover:underline"
        onClick={() =>
          tab === "reports"
            ? navigate(`/dashboard/reports/${item.reportId}`)
            : navigate(`/dashboard/retests/${item.retestId}`)
        }
      >
        {tab === "reports" ? item.reportName : item.reportTitle}
      </div>
      <div className="col-span-3">
        {tab === "reports" ? (
          <SeverityBadge severity={item.severityCategory} />
        ) : (
          <StatusBadge status={item.status} />
        )}
      </div>
      <div className="col-span-2 text-gray-500">
        {tab === "reports"
          ? formatDate(item.submittedDate)
          : formatDate(item.createdAt ?? (item as any).created_at ?? (item as any).updated_at)}
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white h-[480px] flex flex-col">
      {renderHeader()}
      <div className="p-4 flex-1 flex flex-col min-h-0">
        {renderTableHead()}
        <div className="flex-1 overflow-y-auto">
          {(items || []).slice(0, 3).map(renderRow)}
          {(items || []).slice(3).map(renderRow)}
        </div>
      </div>
    </div>
  );
}


